import { useMemo } from "react"
import { Flex, Text, Tag, Icon, AIBadgeScore, Tooltip, Card, Divider, Skeleton, Spinner } from "components/common"
import { Select, Timeline } from "antd"
import styles from "./index.module.less"
import { t } from "i18next"
import theme from "utils/themeVars"
import UserProfileSummary from "features/shared/components/userProfileSummary"

const NEAR_END_SCROLL_PX = 80

const TAG_COLORS = {
  Relevant: { bg: "#e6f7e6", color: "#52c41a" },
  Irrelevant: { bg: "#fff7e6", color: "#faad14" },
  Inappropriate: { bg: "#fff1f0", color: "#ff4d4f" },
}

const SegmentFilterOptionLabel = ({ staffName, formattedDate }) => (
  <Flex justify="space-between" align="center" gap="12px" style={{ width: "100%" }}>
    <Text className="fs14 gray-800">{staffName}</Text>
    <Text className="fs14" type="secondary">
      {formattedDate}
    </Text>
  </Flex>
)

const mapSegmentDropdownOptionsWithLayout = segmentOptions =>
  segmentOptions?.map(option => {
    if (option?.value === "all") {
      return option
    }
    return {
      ...option,
      label: (
        <SegmentFilterOptionLabel staffName={option?.staffName} formattedDate={option?.formattedDate} />
      ),
    }
  }) 

const conversationHistorySkeleton = (
  <Flex vertical className={styles.conversationContainer} style={{ gap: 15 }}>
    <div className={styles.scrollWrapper}>
      <Flex vertical style={{ gap: 12 }}>
        {[1, 2, 3].map(slot => (
          <Skeleton key={slot} type="button" width={700} height={30} active />
        ))}
      </Flex>
    </div>
  </Flex>
)

const ConversationHistory = ({
  conversations = [],
  segmentOptions = [],
  selectedSegmentId = "all",
  onSegmentChange,
  loading = false,
  loadingFurther = false,
  onReachEnd,
  scoresBreakdown = {},
}) => {
  const getThemeScoreRows = conversationTheme => scoresBreakdown?.[conversationTheme]?.scores ?? []

  const segmentDropdownChoices = useMemo(
    () => mapSegmentDropdownOptionsWithLayout(segmentOptions),
    [segmentOptions],
  )

  const handleScroll = e => {
    if (loadingFurther || !onReachEnd) return
    const root = e?.currentTarget
    if (root?.scrollHeight - root?.scrollTop - root?.clientHeight <= NEAR_END_SCROLL_PX) {
      onReachEnd()
    }
  }

  if (loading) {
    return conversationHistorySkeleton
  }

  if (!conversations || conversations.length === 0) {
    return (
      <Flex vertical className={styles.conversationContainer}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={8} className={styles.header}>
          <Text className="bold fs16">{t("Conversation History")}</Text>
          <Flex gap={8} wrap="wrap">
            <Select
              value={selectedSegmentId}
              onChange={onSegmentChange}
              options={segmentDropdownChoices}
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 360 } } }}
              style={{ minWidth: 280, maxWidth: 400 }}
            />
          </Flex>
        </Flex>
        <Text type="secondary">{t("No conversation history available")}</Text>
      </Flex>
    )
  }

  return (
    <Flex vertical className={styles.conversationContainer}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} className={styles.header}>
        <Text className="bold fs16">{t("Conversation History")}</Text>
        <Flex gap={8} wrap="wrap">
          <Select
            value={selectedSegmentId}
            onChange={onSegmentChange}
            options={segmentDropdownChoices}
            popupMatchSelectWidth={false}
            styles={{ popup: { root: { minWidth: 360 } } }}
            style={{ minWidth: 280, maxWidth: 400 }}
          />
        </Flex>
      </Flex>

      <div className={styles.scrollWrapper} onScroll={handleScroll}>
        <Timeline
          className={styles.timeline}
          items={conversations?.map(item => {
            const engagementScoreModel = item?.engagementScore
            const tagColor = TAG_COLORS?.[item?.tag] || TAG_COLORS?.Relevant
            const breakdownRows =
              item?.engagementBreakdown?.length > 0 ? item?.engagementBreakdown : getThemeScoreRows(item?.theme)
            const badge = engagementScoreModel ? (
              <AIBadgeScore
                totalScore={engagementScoreModel?.display}
                title={t("Score:")}
                buttonProps={{ style: { height: 26 }, size: "small", shape: "round" }}
              />
            ) : null

            return {
              key: item?.timelineRowKey,
              dot: <Icon icon="BsDot" size={35} color={theme["gray700"]} />,
              children: (
                <Flex vertical className={styles.timelineContent}>
                  <Text className="fs14">
                    <span className="bold">{item?.formattedDate}</span>
                    <span className="gray-600" style={{ marginInlineStart: 4 }}>
                      ({item?.relativeDate})
                    </span>
                  </Text>

                  <Flex justify="space-between" align="center" className="mbe-10">
                    <Flex align="center" gap="5px">
                      <Text className="fs14 gray-800">{item?.staffName}</Text>
                      <UserProfileSummary staffId={item?.staffId} />
                    </Flex>

                    {breakdownRows?.length > 0 && engagementScoreModel ? (
                      <Tooltip
                        placement="bottomLeft"
                        color="white"
                        overlayInnerStyle={{ padding: 0 }}
                        title={
                          <Card size="small" style={{ width: 300, padding: "6px 6px" }}>
                            <Text className="bold fs14">{t("Engagement Score")}</Text>
                            <Divider style={{ margin: "4px 0" }} />
                            {breakdownRows.map((row, i) => (
                              <Flex key={i} justify="space-between" align="center" className="mbe-8">
                                <Text className="fs14 gray-800">{row?.label}</Text>
                                <Text
                                  className="fs14"
                                  style={{ color: row?.percent < 50 ? "red" : theme["darkThemeGray800"] }}
                                >
                                  {row?.score}/{row?.total}
                                </Text>
                              </Flex>
                            ))}
                          </Card>
                        }
                      >
                        <span>{badge}</span>
                      </Tooltip>
                    ) : (
                      badge
                    )}
                  </Flex>

                  <Text className="fs14 gray-900 mbe-10">{item?.summary}</Text>

                  <Flex className={styles.tags} align="center" wrap="wrap" gap="4px">
                    {item?.tag && (
                      <Tag
                        pill
                        style={{
                          "--tag-bg": tagColor?.bg,
                          color: tagColor?.color,
                          height: 25,
                        }}
                      >
                        <Text style={{ color: tagColor?.color }} className="fs14">
                          {item?.tag}
                        </Text>
                      </Tag>
                    )}
                    {item?.theme && (
                      <Tag
                        pill
                        style={{
                          "--tag-bg": theme["gray200"],
                          height: 25,
                        }}
                      >
                        <Text className="fs14">{item?.theme}</Text>
                      </Tag>
                    )}
                  </Flex>
                </Flex>
              ),
            }
          })}
        />
        {loadingFurther ? (
          <Flex justify="center" className={styles.timelineFurtherSpinner}>
            <Spinner size="small" />
          </Flex>
        ) : null}
      </div>
    </Flex>
  )
}

export default ConversationHistory
